<script setup>
import { computed, ref, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useMenuStore } from '../stores/menuStore'
import FixturesPage from '../views/FixturesPage.vue'
import PlayerProfile from './PlayerProfile.vue'
import TeamRosterPanel from './TeamRosterPanel.vue'

const menuStore = useMenuStore()
const isComponentMounted = ref(false)

const getThirdMenuComponent = computed(() => {
  if (!isComponentMounted.value) return null
  
  switch (menuStore.thirdMenuType) {
    case 'fixture':
      return FixturesPage
    case 'player':
      return PlayerProfile
    case 'roster':
      return TeamRosterPanel
    default:
      return FixturesPage
  }
})

const isRoster = computed(() => {
  return isComponentMounted.value && menuStore.thirdMenuType === 'roster'
})

const leagueName = computed(() => {
  if (!isComponentMounted.value || !menuStore.thirdMenuContent) return ''
  return menuStore.thirdMenuContent?.league?.league_name || ''
})

const teamName = computed(() => {
  if (!isComponentMounted.value || !menuStore.thirdMenuContent) return ''
  return menuStore.thirdMenuContent?.team?.team?.team_name || menuStore.thirdMenuContent?.team?.name || ''
})

// Safe close function with guards
const safeCloseThirdMenu = () => {
  if (isComponentMounted.value) {
    menuStore.closeThirdMenu()
  }
}

onMounted(async () => {
  await nextTick()
  isComponentMounted.value = true
})

onBeforeUnmount(() => {
  isComponentMounted.value = false
})

defineProps({
  showSecondaryMenu: {
    type: Boolean,
    required: true
  },
  isSidebarCollapsed: {
    type: Boolean,
    required: true
  }
})



</script>

<template>
  <div 
    v-if="menuStore.showThirdMenu && isComponentMounted" 
    class="fixed inset-0 bg-black/50 p-2 z-[100] transition-all duration-300"
    :class="[
      showSecondaryMenu 
        ? (isSidebarCollapsed ? 'left-80' : 'left-[34rem]')
        : (isSidebarCollapsed ? 'left-16' : 'left-64'),
    ]"
  >
    <div class="bg-white rounded-xs h-full w-full shadow-2xl relative overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
        <div class="flex items-center space-x-4">
          <!-- <div class="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center">
            <i class="fas fa-info-circle text-pink-500 text-xl"></i>
          </div> -->

          

          <div v-if="isRoster && menuStore.thirdMenuContent" class="flex flex-col gap-1">
            <div class="flex gap-8">
              <div v-if="leagueName" class="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
                <i class="fas fa-trophy text-pink-500"></i>
                <span class="text-sm font-medium text-gray-600">{{ leagueName }}</span>
              </div>
              <div class="flex items-center">
                <i class="fas fa-chevron-right text-gray-400 mx-2"></i>
              </div>
              <div class="flex items-center justify-between gap-8">
                <div v-if="menuStore.thirdMenuContent.team?.team" class="flex items-center gap-3 bg-gray-50 px-2 rounded-lg">
                  <div class="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center">
                    <i class="fas fa-shield-alt text-pink-500"></i>
                  </div>
                  <span class="font-bold text-xl text-gray-800">{{ menuStore.thirdMenuContent.team.team.team_name }}</span>
                </div>
                <div class="flex items-center">
                  <i class="fas fa-chevron-right text-gray-400 mx-2"></i>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-pink-500 bg-pink-50 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                    <i class="fas fa-users"></i>
                    Roster Management
                  </span>
                </div>
              </div>
            </div>
          </div>
          <!-- <div v-else-if="menuStore.thirdMenuContent?.name">
            <h3 class="font-semibold text-gray-800 text-lg">{{ menuStore.thirdMenuContent.name.player_name || menuStore.thirdMenuContent.name }}</h3>
            <p class="text-sm text-gray-500">{{ menuStore.thirdMenuContent}}</p>
          </div>
          <div v-else>
            <h3 class="font-semibold text-gray-800 text-lg">Loading...</h3>
          </div> -->
        </div>
        <div class="flex items-center space-x-3">
          <!-- <button 
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <i class="fas fa-sync-alt"></i>
          </button> -->
          <button 
            @click="safeCloseThirdMenu"
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-hidden">
        <div class="h-full flex">
          <!-- Left Sidebar -->
          <div class="hidden w-64 border-r border-gray-200 bg-gray-50 p-4">
            <div class="space-y-4">
              <div class="space-y-2">
                <h4 class="text-sm font-medium text-gray-500 uppercase tracking-wider">Quick Actions</h4>
                <div class="space-y-1">
                  <button class="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-white rounded-lg transition-colors">
                    <i class="fas fa-edit w-5"></i>
                    <span>Edit Details</span>
                  </button>
                  <button class="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-white rounded-lg transition-colors">
                    <i class="fas fa-copy w-5"></i>
                    <span>Duplicate</span>
                  </button>
                  <button class="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-white rounded-lg transition-colors">
                    <i class="fas fa-share-alt w-5"></i>
                    <span>Share</span>
                  </button>
                </div>
              </div>

              <div class="space-y-2">
                <h4 class="text-sm font-medium text-gray-500 uppercase tracking-wider">Status</h4>
                <div class="bg-white rounded-lg p-3">
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-gray-600">Status</span>
                    <span class="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Active</span>
                  </div>
                  <div class="mt-2 flex items-center justify-between">
                    <span class="text-sm text-gray-600">Last Updated</span>
                    <span class="text-sm text-gray-500">2h ago</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Main Content -->
          <div class="flex-1 flex overflow-y-auto">
            <div class="p-6 flex-1 bg-gray-50">
              <component 
                v-if="getThirdMenuComponent && menuStore.thirdMenuContent"
                :is="getThirdMenuComponent" 
                :content="menuStore.thirdMenuContent"
                class="bg-white rounded-lg"
              />
              <div v-else class="bg-white rounded-lg p-6 flex items-center justify-center">
                <div class="text-center">
                  <i class="fas fa-spinner fa-spin text-gray-400 text-2xl mb-2"></i>
                  <p class="text-gray-500">Loading content...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

/* Add smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}

/* Add hover effects */
.hover\:bg-white:hover {
  background-color: white;
}

.hover\:text-gray-700:hover {
  color: #374151;
}

/* Add shadow effects */
.shadow-2xl {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}
</style> 